import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraInput, SpecraSegmentedControl, SpecraTag, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { isShadowed, overriddenLayer, useDeleteSop, useSaveSop, useSops } from '../../hooks/useSops';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { SOP_MAX_WHEN, validateSop } from '../../lib/chat/sopFormat';
import { EnhancedMarkdown } from '../EnhancedMarkdown';
const LAYERS = [
    {
        layer: 'personal',
        label: 'Personal'
    },
    {
        layer: 'organization',
        label: 'Organization'
    },
    {
        layer: 'built-in',
        label: 'Built-in'
    }
];
const EMPTY = {
    name: '',
    title: '',
    when: '',
    body: '',
    scope: 'personal'
};
function SopEditor({ draft, onCancel, onSaved }) {
    const [d, setD] = useState(draft);
    const [error, setError] = useState('');
    const save = useSaveSop();
    const set = (k)=>(e)=>setD((prev)=>({
                    ...prev,
                    [k]: e.target.value
                }));
    const submit = async ()=>{
        const fields = {
            ...d,
            name: d.name.trim(),
            title: d.title.trim(),
            when: d.when.trim()
        };
        const invalid = validateSop(fields);
        if (invalid) {
            setError(invalid);
            return;
        }
        setError('');
        try {
            await save.mutateAsync(fields);
            onSaved();
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save the SOP.'));
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-2",
        "data-testid": Testids.memory.sopEditor,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        className: "font-medium",
                        children: d.id ? `Edit ${d.name}` : 'New SOP'
                    }),
                    d.id ? /*#__PURE__*/ _jsx(SpecraTag, {
                        tone: d.scope === 'organization' ? 'warning' : 'info',
                        children: d.scope === 'organization' ? 'Organization' : 'Personal'
                    }) : /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Who it applies to",
                        size: "sm",
                        value: d.scope,
                        onValueChange: (scope)=>setD((prev)=>({
                                    ...prev,
                                    scope
                                })),
                        options: [
                            {
                                value: 'personal',
                                label: 'Personal'
                            },
                            {
                                value: 'organization',
                                label: 'Organization'
                            }
                        ]
                    })
                ]
            }),
            d.scope === 'organization' ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Applies to everyone in the organization. Only an admin or Company Settings admin can save it."
            }) : null,
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": "Name",
                placeholder: "name (kebab-case, e.g. past-performance)",
                value: d.name,
                onChange: set('name'),
                "data-testid": Testids.memory.sopNameInput
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": "Title",
                placeholder: "Title",
                value: d.title,
                onChange: set('title')
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": "When",
                placeholder: "When should it be used? (one line)",
                maxLength: SOP_MAX_WHEN,
                value: d.when,
                onChange: set('when')
            }),
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                "aria-label": "Body",
                placeholder: "The procedure, in markdown",
                rows: 8,
                value: d.body,
                onChange: set('body'),
                className: "font-mono text-xs"
            }),
            error ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "danger",
                role: "alert",
                "data-testid": Testids.memory.sopError,
                children: error
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: onCancel,
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: submit,
                        isLoading: save.isPending,
                        disabled: save.isPending,
                        "data-testid": Testids.memory.sopSave,
                        children: "Save"
                    })
                ]
            })
        ]
    });
}
function SopItem({ row, rows, onEdit, onCustomize }) {
    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState('');
    const del = useDeleteSop();
    const overrides = overriddenLayer(row, rows);
    const shadowed = isShadowed(row, rows);
    const editable = row.layer !== 'built-in';
    const remove = async ()=>{
        setError('');
        try {
            await del.mutateAsync(row.id);
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not delete the SOP.'));
            setConfirming(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border border-border p-3",
        "data-testid": Testids.memory.sopItem,
        "data-layer": row.layer,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "link",
                                        size: "inline",
                                        className: "font-mono text-sm text-foreground",
                                        "aria-expanded": open,
                                        title: open ? 'Hide the procedure' : 'Show the procedure',
                                        onClick: ()=>setOpen((v)=>!v),
                                        children: row.name
                                    }),
                                    overrides ? /*#__PURE__*/ _jsxs(SpecraTag, {
                                        tone: "info",
                                        children: [
                                            "overrides ",
                                            overrides
                                        ]
                                    }) : null,
                                    shadowed ? /*#__PURE__*/ _jsx(SpecraTag, {
                                        children: "overridden"
                                    }) : null
                                ]
                            }),
                            row.title && row.title !== row.name ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "body-sm",
                                className: "mt-0.5 block",
                                children: row.title
                            }) : null,
                            row.when ? /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                className: "mt-0.5 block",
                                children: [
                                    "When: ",
                                    row.when
                                ]
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex shrink-0 gap-1",
                        children: editable ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: "Edit SOP",
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: onEdit,
                                    children: /*#__PURE__*/ _jsx(Pencil, {
                                        className: "h-3.5 w-3.5"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: "Delete SOP",
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>setConfirming(true),
                                    children: /*#__PURE__*/ _jsx(Trash2, {
                                        className: "h-3.5 w-3.5"
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            iconLeft: /*#__PURE__*/ _jsx(Copy, {}),
                            onClick: onCustomize,
                            children: "Customize"
                        })
                    })
                ]
            }),
            confirming ? /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex items-center justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            "Delete the ",
                            row.layer,
                            " SOP ",
                            row.name,
                            "?"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: ()=>setConfirming(false),
                        children: "Keep"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "danger",
                        onClick: remove,
                        isLoading: del.isPending,
                        disabled: del.isPending,
                        children: "Delete"
                    })
                ]
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "danger",
                role: "alert",
                className: "mt-2 block",
                "data-testid": Testids.memory.sopError,
                children: error
            }) : null,
            open ? /*#__PURE__*/ _jsx("div", {
                className: "mt-2 border-t border-border pt-2",
                children: /*#__PURE__*/ _jsx(EnhancedMarkdown, {
                    content: row.body,
                    className: "prose-sm",
                    density: "chat"
                })
            }) : null
        ]
    });
}
/** The SOP library: built-in, organization and personal procedures the agent follows. */ export function SopsTab() {
    const sops = useSops();
    const [draft, setDraft] = useState(null);
    const rows = sops.data ?? [];
    if (draft) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex-1 overflow-y-auto p-5",
            children: /*#__PURE__*/ _jsx(SopEditor, {
                draft: draft,
                onCancel: ()=>setDraft(null),
                onSaved: ()=>setDraft(null)
            }, draft.id ?? `new:${draft.name}`)
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex-1 overflow-y-auto p-5",
        "data-testid": Testids.memory.sopList,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Procedures the assistant follows. Personal overrides organization, which overrides built-in."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {}),
                        onClick: ()=>setDraft(EMPTY),
                        "data-testid": Testids.memory.sopNew,
                        children: "New SOP"
                    })
                ]
            }),
            sops.isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading…"
            }) : null,
            sops.error ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "danger",
                role: "alert",
                "data-testid": Testids.memory.sopError,
                children: apiErrorMessage(sops.error, 'Could not load SOPs.')
            }) : null,
            LAYERS.map(({ layer, label })=>{
                const group = rows.filter((r)=>r.layer === layer);
                if (group.length === 0) return null;
                return /*#__PURE__*/ _jsxs("section", {
                    className: "mb-4",
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            className: "mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground",
                            children: label
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col gap-2",
                            children: group.map((row)=>/*#__PURE__*/ _jsx(SopItem, {
                                    row: row,
                                    rows: rows,
                                    onEdit: ()=>setDraft({
                                            id: row.id,
                                            scope: row.layer === 'organization' ? 'organization' : 'personal',
                                            name: row.name,
                                            title: row.title,
                                            when: row.when,
                                            body: row.body
                                        }),
                                    onCustomize: ()=>setDraft({
                                            scope: 'personal',
                                            name: row.name,
                                            title: row.title,
                                            when: row.when,
                                            body: row.body
                                        })
                                }, row.id))
                        })
                    ]
                }, layer);
            }),
            !sops.isLoading && !sops.error && rows.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No SOPs yet."
            }) : null
        ]
    });
}
